/**
 * /stamps — public gallery of every custom crest / seal Local Guides
 * have uploaded, grouped by "Country · State" for a natural
 * district-first browse. Server-side sort keeps the ordering stable.
 *
 * Fetches `GET /api/geo/places/stamps/gallery` — see
 * `backend/routes/geo_places.py`. Optional `?country=` and `?kind=`
 * filters narrow the roster.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Stamp, Filter } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const KINDS = [
  { slug: "",        label: "All kinds" },
  { slug: "state",   label: "States" },
  { slug: "district",label: "Districts" },
  { slug: "county",  label: "Counties" },
  { slug: "city",    label: "Cities" },
  { slug: "town",    label: "Towns" },
];

const COUNTRIES = [
  { slug: "",              label: "Every country" },
  { slug: "India",         label: "India" },
  { slug: "United States", label: "United States" },
];

export default function StampGallery() {
  const [params, setParams] = useSearchParams();
  const country = params.get("country") || "";
  const kind    = params.get("kind") || "";

  const [data, setData] = useState({ count: 0, groups: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const query = new URLSearchParams();
    if (country) query.set("country", country);
    if (kind)    query.set("kind", kind);
    axios.get(`${API}/api/geo/places/stamps/gallery?${query.toString()}`)
      .then((r) => alive && setData(r.data || { count: 0, groups: [] }))
      .catch(() => alive && setData({ count: 0, groups: [] }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [country, kind]);

  const total = data.count || 0;
  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-10" data-testid="stamp-gallery-page">
      <div className="flex items-center gap-2 mb-2">
        <Stamp className="w-6 h-6" style={{ color: "var(--wofy-primary)" }} />
        <h1 className="font-display text-3xl">Custom City Crests</h1>
      </div>
      <p className="text-sm opacity-70 max-w-[64ch] mb-6">
        Real crests, seals and emblems uploaded by Local Guides for their
        claimed cities. Tap any stamp to open the location — or claim your
        own city in the <Link to="/guides" className="underline" data-testid="stamp-gallery-guides-link">Guide Console</Link>.
      </p>

      {/* Filters */}
      <div className="wofy-card mb-8" data-testid="stamp-gallery-filters">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
          <Filter className="w-3 h-3 inline -mt-0.5 mr-1" /> FILTER
        </div>
        <div className="flex flex-wrap gap-4 items-center text-sm">
          <label className="inline-flex items-center gap-2">
            <span className="opacity-70">Country</span>
            <select
              value={country}
              onChange={(e) => setFilter("country", e.target.value)}
              className="rounded-md border px-2 py-1 bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="stamp-gallery-country"
            >
              {COUNTRIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
            </select>
          </label>
          <label className="inline-flex items-center gap-2">
            <span className="opacity-70">Kind</span>
            <select
              value={kind}
              onChange={(e) => setFilter("kind", e.target.value)}
              className="rounded-md border px-2 py-1 bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="stamp-gallery-kind"
            >
              {KINDS.map((k) => <option key={k.slug} value={k.slug}>{k.label}</option>)}
            </select>
          </label>
          <span className="opacity-60 text-xs font-mono ml-auto">
            {loading ? "…" : `${total} custom crest${total === 1 ? "" : "s"}`}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="opacity-70 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
      ) : total === 0 ? (
        <div className="wofy-card text-sm opacity-70" data-testid="stamp-gallery-empty">
          No custom crests yet for this filter. Approved guides can upload
          one from their city's <Link to="/guides" className="underline">console</Link>.
        </div>
      ) : (
        <div className="space-y-10" data-testid="stamp-gallery-groups">
          {data.groups.map((g) => (
            <section key={g.key} data-testid={`stamp-gallery-group-${g.key}`}>
              <h2 className="font-display text-xl mb-3">{g.key}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {g.items.map((it) => (
                  <Link
                    key={it.slug}
                    to={`/l/${it.slug}`}
                    className="group text-center"
                    data-testid={`stamp-gallery-item-${it.slug}`}
                  >
                    <img
                      src={it.stamp_url}
                      alt={`${it.name} crest`}
                      loading="lazy"
                      className="mx-auto w-24 h-24 rounded-full object-cover shadow-md ring-2 ring-white group-hover:-translate-y-0.5 transition"
                    />
                    <div className="mt-2 font-medium text-sm truncate group-hover:underline">
                      {it.name}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mt-0.5">
                      {(it.kind || "").toUpperCase()}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
